import type { Transition } from "@uirouter/core";
import { UIView } from "@uirouter/react";

import {
    FIND_LEADS_SAVED_SEARCH_ABSTRACT_ROUTE,
    FIND_LEADS_SAVED_SEARCH_ROUTE,
    FIND_LEADS_SEARCH_ROUTE,
    FIND_LEADS_SEARCH_ROUTE_URL,
    SAVE_SEARCH_RELATIVE_ROUTE_URL,
    SI_ROOT_ROUTE,
} from "app/pages/sales-intelligence/constants/routes";
import { leadGeneratorRoutingHooks } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/routing";
import { filtersRoutingParams, searchResultsParams } from "app/pages/sales-intelligence/modules/leads-search/constants";
import { savedSearchParams } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/constants";
import { SEARCH_FILTERS_ROUTE_PARAMS } from "app/pages/sales-intelligence/modules/leads-search-filters/constants";
import { EmailTrackingEntityType } from "app/pages/sales-intelligence/services/enums";
import emailTrackingService, {
    type PageViewTrackingParams,
} from "app/pages/sales-intelligence/services/tracking/emailTrackingService";
import { signalsRoutingParams } from "app/pages/sales-intelligence/sub-modules/signals/constants";
import { SwTrack } from "app/services/SwTrack";
import { markPageViewIntentionallySkipped } from "app/services/track/pageViewTrackSignal";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";

import PageRootContainer from "../containers/PageRootContainer/PageRootContainer";
import { pageParams } from "../types";

import { onEnterAdvancedSearchPage } from "./route-hooks";

const SEARCH_ROUTE_PARAMS = {
    ...pageParams,
    ...filtersRoutingParams,
    findNewProspects: { dynamic: true },
    signalId: { dynamic: true },
    isShowSavedSearchList: { dynamic: true },
    ...SEARCH_FILTERS_ROUTE_PARAMS,
};
const SAVED_SEARCH_ROUTE_PARAMS = {
    ...pageParams,
    ...searchResultsParams,
    ...filtersRoutingParams,
    ...savedSearchParams,
    ...signalsRoutingParams,
};

export const ADVANCED_SEARCH_STATE_CONFIG: Record<string, IRouterState> = {
    [FIND_LEADS_SAVED_SEARCH_ABSTRACT_ROUTE]: {
        abstract: true,
        parent: SI_ROOT_ROUTE,
        url: FIND_LEADS_SEARCH_ROUTE_URL,
        component(...props: unknown[]) {
            return (
                <div className="sw-layout-scrollable-element use-sticky-css-rendering">
                    {/* @ts-expect-error unknown[] passed as UIViewProps */}
                    <UIView {...props} />
                </div>
            );
        },
        configId: "SalesWorkspace",
        configIdDeps: ["WebAnalysis", "MobileWeb", "MobileWebWorldwide", "LeadGenerationReport"],
        packageName: "salesIntelligence",
        stateNameForEductionBar: "salesIntelligence-advancedSearch-abstract",
        stateId: "salesIntelligence-advancedSearch-abstract",
        secondaryBarType: "SalesIntelligenceFind",
        isSecondaryBarOpen: false,
        params: {
            // to avoid unmount of components during changing of query params
            ...SAVED_SEARCH_ROUTE_PARAMS,
            ...SEARCH_ROUTE_PARAMS,
        },
    },
    [FIND_LEADS_SEARCH_ROUTE]: {
        parent: FIND_LEADS_SAVED_SEARCH_ABSTRACT_ROUTE,
        url: "",
        onEnter(transition: Transition) {
            onEnterAdvancedSearchPage();
            void leadGeneratorRoutingHooks.onEnterPrefetchThread(transition);
        },
        component: PageRootContainer,
        configId: "SalesWorkspace",
        configIdDeps: ["LeadGenerator", "LeadGenerationReport"],
        isSecondaryBarOpen: false,
        secondaryBarType: "SalesIntelligenceFind",
        pageTitle: "si.pages.find_leads.document_title",
        stateNameForEductionBar: "salesIntelligence-advancedSearch-abstract",
        stateId: "salesIntelligence-advancedSearch-abstract",
        trackingId: {
            section: "SI_find",
            subSection: "lead gen",
            subSubSection: "find websites",
        },

        params: {
            ...SEARCH_ROUTE_PARAMS,
            // the savedSearchId is populated from parent state, reset on navigation
            savedSearchId: savedSearchParams.savedSearchId,
        },
    },
    [FIND_LEADS_SAVED_SEARCH_ROUTE]: {
        parent: FIND_LEADS_SAVED_SEARCH_ABSTRACT_ROUTE,
        url: SAVE_SEARCH_RELATIVE_ROUTE_URL,
        onEnter(transition: Transition) {
            onEnterAdvancedSearchPage();
            void leadGeneratorRoutingHooks.onEnterPrefetchThread(transition);
        },
        component: PageRootContainer,
        configId: "SalesWorkspace",
        configIdDeps: ["LeadGenerator", "LeadGenerationReport"],
        stateNameForEductionBar: "salesIntelligence-advancedSearch-abstract",
        stateId: "salesIntelligence-advancedSearch-abstract",
        trackingId: {
            section: "SI_find",
            subSection: "lead gen",
            subSubSection: "saved search",
        },
        data: {
            pageViewTracking: function ({ toParams, toState }: PageViewTrackingParams) {
                emailTrackingService.trackSignalsDigestEmail(EmailTrackingEntityType.SAVED_SEARCHES, toParams);

                if (!toParams.signalId) {
                    void SwTrack.trackPageView(toState, toParams);
                } else {
                    markPageViewIntentionallySkipped();
                }
            },
        },
        pageTitle: "si.pages.find_leads.document_title",
        isSecondaryBarOpen: false,
        secondaryBarType: "SalesIntelligenceFind",
        params: SAVED_SEARCH_ROUTE_PARAMS,
    },
};
